'use client';

import { useState, useEffect, useRef } from 'react';
import { MultiSelectVendedores } from '../components/MultiSelectVendedores';
import { ComparisonTable } from '../components/ComparisonTable';
import { ComparisonCharts } from '../components/ComparisonCharts';
import Link from 'next/link';

interface ComparisonData {
  vendedor_id: number;
  vendedor_nome: string;
  vendedores_quantidade: number;
  vendedores_valor: number;
  vendedores_media: number;
  vendedores_desvio_padrao: number;
  tickets_quantidade: number;
  tickets_tempo_medio_horas: number;
  tickets_espera_media_minutos: number;
  tickets_transferencias_media: number;
  tickets_mensagens_media_minutos: number;
  relacao_espera_venda: number;
}

interface GroupMetrics {
  total_vendedores: number;
  media_quantidade_vendas: number;
  media_valor_vendas: number;
  media_tickets: number;
  media_espera_minutos: number;
}

export default function ComparisonPage() {
  const [comparisons, setComparisons] = useState<ComparisonData[]>([]);
  const [groupMetrics, setGroupMetrics] = useState<GroupMetrics | null>(null);
  const [selectedVendedores, setSelectedVendedores] = useState<number[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  // Set default date range
  useEffect(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);


  let closeVendedoresDropdown = () => {
    console.warn('closeVendedoresDropdown function not set yet');
  }

  const fetchComparison = async () => {
    closeVendedoresDropdown()


    if (selectedVendedores.length === 0) {
      alert('Selecione pelo menos um vendedor');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        vendedores: selectedVendedores.join(','),
      });

      const response = await fetch(`/api/analytics/comparison?${params}`);
      const data = await response.json();

      if (data.comparisons) {
        setComparisons(data.comparisons);
        setGroupMetrics(data.groupMetrics);
      }
    } catch (error) {
      console.error('Error fetching comparison:', error);
      alert('Erro ao carregar dados de comparação');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    if (comparisons.length === 0) {
      alert('Nenhum dado para exportar');
      return;
    }

    setExporting(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        vendedores: selectedVendedores.join(','),
      });

      const response = await fetch(`/api/analytics/comparison/export-pdf?${params}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`PDF export failed: ${errorData.error || response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comparacao-vendedores-${startDate}-${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert(`Erro ao exportar PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    if (comparisons.length === 0) {
      alert('Nenhum dado para exportar');
      return;
    }

    setExporting(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        vendedores: selectedVendedores.join(','),
      });

      const response = await fetch(`/api/analytics/comparison/export-excel?${params}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Excel export failed: ${errorData.error || response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comparacao-vendedores-${startDate}-${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert(`Erro ao exportar Excel: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <main
      style={{ backgroundColor: 'var(--background)' }}
    >
      <div className="py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="w-full max-w-6xl">
          {/* Header with Navigation */}
          <div className="mb-10 text-center">
            <h1 className="text-5xl font-bold mb-3 text-slate-200 my-4" style={{
              margin: '1em'
            }}>Comparação de Vendedores</h1>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-4"></div>
            <div className="flex justify-center gap-4 mt-6">
              <Link
                href="/"
                className="px-6 py-2 rounded-lg font-medium transition-all border"
                style={{
                  backgroundColor: 'rgba(148, 163, 184, 0.2)',
                  color: '#e2e8f0',
                  borderColor: 'var(--card-border-bright)',
                  margin: '1em',
                  padding: '0.5em 1.5em',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.2)';
                }}
              >
                ← Voltar ao Dashboard
              </Link>
            </div>
          </div>

        {/* Filters Section */}
        <div
          className="rounded-lg border p-6 mb-8"
          style={{
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border-bright)',
            backdropFilter: 'blur(10px)',
            padding: '1em'
          }}
        >
          <h2 className="text-lg font-bold mb-6 text-center p-4">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Data Inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-700/30 border text-slate-200 text-center"
                style={{ borderColor: 'var(--card-border-bright)', padding: '0.5em 1.5em' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Data Final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-700/30 border text-slate-200 text-center"
                style={{ borderColor: 'var(--card-border-bright)', padding: '0.5em 1.5em' }}
              />
            </div>

            <div className="md:col-span-1">
              <MultiSelectVendedores closeVendedoresDropdown={closeVendedoresDropdown} onVendedoresChange={setSelectedVendedores} />
            </div>

            <div className="flex items-end">
              <button
                onClick={fetchComparison}
                disabled={loading}
                className="w-full px-4 py-3 rounded-lg font-medium transition-all border disabled:opacity-50"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff',
                  borderColor: 'var(--accent)',
                  padding: '0.5em 1.5em',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'var(--accent)';
                  }
                }}
              >
                {loading ? 'Carregando...' : 'Comparar'}
              </button>
            </div>
          </div>
        </div>

        {/* Export Buttons */}
        {comparisons.length > 0 && (
          <div className="flex gap-4 mb-8 justify-center flex-wrap">
            <button
              onClick={exportToPDF}
              disabled={exporting}
              className="px-6 py-3 rounded-lg font-medium transition-all border flex items-center gap-2"
              style={{
                margin: '1em',
                padding: '0.5em 1.5em',
                backgroundColor: '#ef4444',
                color: '#fff',
                borderColor: '#ef4444',
              }}
              onMouseEnter={(e) => {
                if (!exporting) {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                }
              }}
              onMouseLeave={(e) => {
                if (!exporting) {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                }
              }}
            >
              📄 Exportar PDF
            </button>
            <button
              onClick={exportToExcel}
              disabled={exporting}
              className="px-6 py-3 rounded-lg font-medium transition-all border flex items-center gap-2"
              style={{
                padding: '0.5em 1.5em',
                margin: '1em',
                backgroundColor: '#10b981',
                color: '#fff',
                borderColor: '#10b981',
              }}
              onMouseEnter={(e) => {
                if (!exporting) {
                  e.currentTarget.style.backgroundColor = '#059669';
                }
              }}
              onMouseLeave={(e) => {
                if (!exporting) {
                  e.currentTarget.style.backgroundColor = '#10b981';
                }
              }}
            >
              📊 Exportar Excel
            </button>
          </div>
        )}

        {/* Results Section */}
        {loading ? (
          <div className="text-center text-slate-400 py-12">Carregando dados...</div>
        ) : comparisons.length > 0 ? (
          <div className="w-full" style={{
            position: 'relative',
            width: '100%'
          }}>
            <div className="space-y-8" style={{
              zIndex: -1,
              position: 'absolute',
              width: '100%'
            }}>
              <div ref={tableRef}><ComparisonTable comparisons={comparisons} groupMetrics={groupMetrics!} /></div>
              <ComparisonCharts comparisons={comparisons} />
            </div>
          </div>
        ) : (
          <div className="text-center text-slate-400 py-12">
            Selecione vendedores e clique em "Comparar" para visualizar os dados
          </div>
        )}
        </div>
      </div>
    </main>
  );
}
